import { has, map, omit, take } from "lodash";
import { AbstractDate, DateMath } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import {
  getIntervalFromDates,
  getLastYearPeriod,
  getPreviousPeriod,
} from "@tootools/date";
import { Clause } from "@radarly/models/clause";
import { getDateFromClause } from "@radarly/models/clause/getter";
import {
  refineClauseWithDate,
  refineClauseWithoutField,
} from "@radarly/models/clause/refiner";
import {
  isDatavizWithEvolutions,
  isHorizontalBarChartDataviz,
} from "@radarly/models/dataviz";
import { getComparaisonPeriodFromFragments } from "@radarly/models/fragment";
import { Card } from "..";
import {
  AIFragment,
  EmptyFragment,
  GlobalFragment,
  SearchFragment,
  isInfluencerFragment,
} from "@radarly/models/search";
import { getFragmentWithContext } from "../fragment";
import { isAICard, isCardWithRequest } from "../is";
import { getMetricsFromCard, getPrimaryMetricFromCard } from "../metric";
import { ComparisonPeriod } from "../period";
import { CardTerm, getTermFromCard } from "../term";
import { TimeUnit } from "./time-unit";

const DEFAULT_QUERY = {
  and: [],
};

export abstract class CardFragmentsFactory {
  public getComparaisonFragments<F extends SearchFragment | AIFragment>(
    fragment: F,
    comparison?: ComparisonPeriod
  ): [F, F] | [F] {
    if (comparison) {
      if (comparison === "previous_period") {
        const { from: fromClause, to: toClause } = getDateFromClause(
          fragment.request.query
        );
        const { from, to } = getPreviousPeriod({
          from: fromClause,
          to: toClause,
        });
        return [
          fragment,
          {
            ...fragment,
            request: {
              ...fragment.request,
              query: refineClauseWithDate(fragment.request.query, {
                from: from,
                to: to || "now",
              }),
            },
          },
        ];
      } else {
        const { from: fromClause, to: toClause } = getDateFromClause(
          fragment.request.query
        );
        const { from, to } = getLastYearPeriod({
          from: fromClause,
          to: toClause,
        });
        return [
          fragment,
          {
            ...fragment,
            request: {
              ...fragment.request,
              query: refineClauseWithDate(fragment.request.query, {
                from: from,
                to: to || "now",
              }),
            },
          },
        ];
      }
    } else {
      return [fragment];
    }
  }
  public getTimeUnitFromInput(options: { timeUnit?: TimeUnit }) {
    return options && options.timeUnit ? options.timeUnit : "default";
  }
  public getMetricFromCardAndInput(
    card: Card,
    options: {
      metric?: Metric;
    }
  ) {
    return options && options.metric
      ? options.metric
      : getPrimaryMetricFromCard(card);
  }
  public getMetricsFromCard(
    card: Card,
    options: {
      metrics?: Metric[];
    }
  ) {
    return options && options.metrics
      ? options.metrics
      : getMetricsFromCard(card);
  }
  public getClauseFromCard(card: Card) {
    if (isCardWithRequest(card)) {
      return refineClauseWithoutField(card.fragments[0].request.query, "date");
    }
    return DEFAULT_QUERY;
  }
  public getComparaisonPeriod(
    card: Card,
    defaultValue: ComparisonPeriod,
    options: { comparisonPeriod?: ComparisonPeriod }
  ) {
    if (!isCardWithRequest(card)) return null;

    if (options && has(options, "comparisonPeriod")) {
      return options.comparisonPeriod;
    } else {
      const [fragment, comparisonFragment] = card.fragments;
      if (!comparisonFragment) {
        return defaultValue;
      } else {
        return getComparaisonPeriodFromFragments(
          fragment,
          comparisonFragment || fragment
        );
      }
    }
  }
  public getPeriod(card: Card) {
    if (isCardWithRequest(card)) {
      return getDateFromClause(card.fragments[0].request.query);
    }
  }
  public getTermFromCard(card: Card, options: { term?: CardTerm }) {
    return options && options.term ? options.term : getTermFromCard(card);
  }
  public getQuery(input: {
    from?: AbstractDate;
    to?: AbstractDate;
    clause?: Clause;
  }) {
    const period = {
      from: input?.from || DateMath.Last_30_Days,
      to: input?.to || DateMath.Now,
    };
    return refineClauseWithDate(input?.clause || DEFAULT_QUERY, period);
  }
  public getInterval(input: {
    from?: AbstractDate;
    to?: AbstractDate;
    clause?: Clause;
  }) {
    return getIntervalFromDates(
      input?.from || DateMath.Last_30_Days,
      input?.to || DateMath.Now,
      "UTC"
    );
  }
}
export class CardFactory<C extends Card> {
  public static getInstance<C extends Card>() {
    return new CardFactory<C>();
  }

  public addIPAiContextToCard<T extends Card>(card: T, IAContext: string): T {
    if (isAICard(card)) {
      return {
        ...card,
        fragments: [
          {
            ...card.fragments[0],
            analysis: {
              ...card.fragments[0].analysis,
            },
            context: IAContext,
          },
        ],
      } as T;
    }
    return card as T;
  }

  public createCard(
    workspaceId: number,
    projectFocuses: number[],
    type: C["type"],
    title: string,
    fragments: C["fragments"],
    dataviz: C["dataviz"]
  ) {
    return {
      type,
      title,
      fragments: this.getValidFragments(
        workspaceId,
        projectFocuses,
        fragments,
        dataviz
      ),
      dataviz: this.getValidDataviz(fragments, dataviz),
    } as C;
  }
  private getValidFragments(
    workspaceId: number,
    projectFocuses: number[],
    fragments: C["fragments"],
    dataviz: C["dataviz"]
  ) {
    if (isDatavizWithEvolutions(dataviz)) {
      return this.getFragmentsWithContext(
        workspaceId,
        projectFocuses,
        fragments
      );
    } else {
      return this.getFragmentsWithContext(
        workspaceId,
        projectFocuses,
        take<GlobalFragment | EmptyFragment>(fragments, 1) as C["fragments"]
      );
    }
  }
  private getFragmentsWithContext(
    workspaceId: number,
    projectFocuses: number[],
    fragments: C["fragments"]
  ) {
    return map(fragments, (fragment: GlobalFragment | EmptyFragment) => {
      return getFragmentWithContext(fragment, workspaceId, projectFocuses);
    });
  }
  private getValidDataviz(fragments: C["fragments"], dataviz: C["dataviz"]) {
    if (isInfluencerFragment(fragments[0])) return dataviz;
    if (isHorizontalBarChartDataviz(dataviz)) {
      const comparisonPeriod = getComparaisonPeriodFromFragments(
        fragments[0],
        fragments[1] || fragments[0]
      );
      if (comparisonPeriod) {
        return {
          ...dataviz,
          secondaryUnit: "evolution",
        };
      } else {
        return omit(dataviz, "secondaryUnit");
      }
    } else {
      return dataviz;
    }
  }
}
